import { Injectable }           from '@angular/core';
import { TranslateService }             from '@ngx-translate/core';
import { NgbModal }                     from '@ng-bootstrap/ng-bootstrap';
import { Spi as SpiClient, TransactionOptions } from '@mx51/spi-client-js';

import { API }                               from '../../app-lite/api/api.constant';
import { ApiService }                        from '../../app-lite/api/api.service';
import { ASSEMBLY_PAYMENT_STATUSES }         from './assembly-payment.statuses.constant';
import { AssemblyPaymentStatusesModel }      from './models/assembly-payment.statuses';
import { AssemblyPaymentsUnpairOptionsModel } from './models/assembly-payments.unpair-options';
import { AssemblyPaymentTerminalModel }      from './models/assembly-payment.terminal';
import { CompanyIntegrationsModel }          from '../../app-lite/companies/models/company.integrations';
import { ConfirmService }                    from '../../app-lite/messages/confirm/confirm.service';
import { CustomerPaymentModel }              from '../../app-lite/customer-payments/models/customer-payment';
import { InvoiceModel }                      from '../../app-lite/invoices/models/invoice';
import { JsonService }                       from '../../app-lite/core/services/json.service';
import { SessionService }                    from '../../app-lite/sessions/session.service';
import { TableQueryParameters }              from '../../app-lite/table/types/table-query-parameters';
import { TableService }                      from '../../app-lite/table/table.service';

import { AssemblyPaymentsPairComponent }       from './assembly-payments.pair.component';
import { AssemblyPaymentsTransactionComponent } from '../../app-lite/assembly-payments/assembly-payments.transaction.component';

@Injectable({ providedIn: 'root' })
export class AssemblyPaymentsService {
  receiptOptions: TransactionOptions;
  spiApi        : SpiClient;

  readonly STATUSES    : AssemblyPaymentStatusesModel = ASSEMBLY_PAYMENT_STATUSES;
  readonly apiKey       = '8XMeAfbitTZYqt637GGHyBKR3sGV04Fc';
  readonly storageKey   = 'assemblyPaymentsTerminal';
  readonly tableCacheKey = 'assemblyPaymentTerminals';
  readonly tenantId     = 'wbc';
  readonly terminalsRoute = '/pos_terminals/assembly_payments_terminal_list';
  readonly vendorKey    = 'workshopsoftware';
  readonly version      = '2.9';

  constructor (
    private apiService      : ApiService,
    private confirmService  : ConfirmService,
    private jsonService     : JsonService,
    private sessionService  : SessionService,
    private ngbModal        : NgbModal,
    private tableService    : TableService,
    private translateService: TranslateService
  ) {}

  activate ( terminal : AssemblyPaymentTerminalModel ) : Promise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', { name: this.getPosName(terminal) }))
    .then(() => this.apiService.get('/company_integration/add_assembly_payments'));
  }

  deactivate ( terminal : AssemblyPaymentTerminalModel ) : Promise<boolean> {
    this.removeTerminalFromStorage();

    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.REMOVE', { name: this.getPosName(terminal) }))
    .then(() => this.apiService.get('/company_integration/remove_assembly_payments'));
  }

  deleteTerminal ( terminal : AssemblyPaymentTerminalModel ) : Promise<boolean> {
    return this.confirmService.generic(this.translateService.instant('ASSEMBLY_PAYMENTS_SPACE.DELETE_PAIRING'))
    .then(() => this.apiService.delete('/pos_terminal', terminal.id))
    .then(() => {
      const currentTerminal = this.getTerminal();

      if (currentTerminal && currentTerminal.id === terminal.id) {
        return this.unpairTerminal(terminal, { skipConfirmation: true });
      }

      return true;
    });
  }

  getPairedTerminal () : Promise<AssemblyPaymentTerminalModel> {
    const terminal = this.getTerminal();

    if (!terminal) {
      return Promise.reject('EFTPOS Payment type used but, terminal is not paired. Please pair terminal in settings.');
    }

    return this.isTerminalPaired(terminal)
      ? Promise.resolve(terminal)
      : Promise.reject('EFTPOS Payment type used but, terminal is currently not connecting to the machine. Check status.');
  }

  getPosName ( terminal : AssemblyPaymentTerminalModel ) : string {
    return this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.ASSEMBLY_PAYMENTS.WESTPAC_GROUP');
  }

  getTerminalCount () : Promise<number> {
    return this.apiService.get('/pos_terminals/assembly_payments_terminal_list_count');
  }

  getTerminal () : AssemblyPaymentTerminalModel {
    return this.jsonService.parse(localStorage.getItem(this.storageKey));
  }

  getTerminals ( params : TableQueryParameters = {} ) : Promise<Array<AssemblyPaymentTerminalModel>> {
    const cache = this.tableService.getCacheForTable(this.tableCacheKey);

    return this.apiService.get(this.terminalsRoute, [
      params.offset           || 0,
      cache.paging.pageSize   || ( params.pageSize  || 5 ),
      cache.sorting.field     || ( params.field     || 'ap_pos_id' ),
      cache.sorting.direction || ( params.direction || 'asc')
    ])
    .then(( terminals : Array<AssemblyPaymentTerminalModel> ) => {
      const currentTerminal = this.getTerminal();

      terminals.map(( terminal : AssemblyPaymentTerminalModel ) => {
        terminal.current = currentTerminal && terminal.id === currentTerminal.id;
        terminal.paired  = terminal.current && this.isTerminalPaired(currentTerminal);
        terminal.status  = ( currentTerminal && terminal.current ) ? currentTerminal.status : '';

        return terminal;
      });

      return Promise.resolve(terminals);
    });
  }

  init () : void {
    this.initializeSpi();
    this.setupListeners();
  }

  initializeSpi ( terminal : AssemblyPaymentTerminalModel = this.getTerminal() ) : void {
    if (terminal) {
      this.spiApi = new SpiClient(
        terminal.ap_pos_id,
        terminal.ap_serial_number,
        terminal.alternate_ip_address || terminal.ap_ip_address,
        terminal.secrets ? {
          EncKey : terminal.secrets.EncKey,
          HmacKey: terminal.secrets.HmacKey
        } : null
      );

      this.spiApi.Config.PromptForCustomerCopyOnEftpos = terminal.ap_print_receipt_from_pos;
      this.spiApi.Config.PrintMerchantCopy             = terminal.ap_print_receipt_from_pos;
      this.spiApi.Config.SignatureFlowOnEftpos         = terminal.ap_print_receipt_from_pos;

      this.spiApi.SetPosInfo(this.vendorKey, this.version);
      this.spiApi.SetTenantCode(this.tenantId);
      this.spiApi.SetDeviceApiKey(this.apiKey);
      this.spiApi.SetSecureWebSockets(true);
      this.spiApi.SetTestMode(this.sessionService.company().pos_terminal_settings.ap_use_test_mode);
      this.spiApi.SetAutoAddressResolution(true);

      this.overrideMethods();
      this.setReceiptOptions();

      try {
        this.spiApi.Start();
      } catch (error) {
        this.unpairTerminal(terminal, { skipConfirmation: true });
      }

      this.spiApi.AckFlowEndedAndBackToIdle();
    }
  }

  isTerminalPaired ( terminal ?: AssemblyPaymentTerminalModel ) : boolean {
    return terminal
      ? terminal.status === this.STATUSES.paired
      : ( this.spiApi
        ? this.spiApi.CurrentStatus === this.STATUSES.paired
        : false
      );
  }

  overrideMethods () : void {
    this.spiApi.BatteryLevelChanged = e => {
      console.log('Battery level changed', e);
    };

    this.spiApi.TerminalConfigurationResponse = e => {
      console.log('Terminal configuration response', e);

      this.spiApi.GetTerminalStatus();
    };

    this.spiApi.TerminalStatusResponse = e => {
      console.log('Terminal status response', e);
    };

    this.spiApi.TransactionUpdateMessage = e => {
      console.log('Transaction update', e);
    };
  }

  pairTerminal ( terminal : AssemblyPaymentTerminalModel ) : Promise<any> {
    const modal = this.ngbModal.open(AssemblyPaymentsPairComponent, { windowClass: 'md-modal' });
    modal.componentInstance.terminal = terminal;
    return modal.result;
  }

  processPayment ( payment : CustomerPaymentModel, invoice = {} as InvoiceModel, amount : number, isRefund = false ) : Promise<any> {
    let data;
    let terminal;

    return this.getPairedTerminal()
    .then(( response : AssemblyPaymentTerminalModel ) => {
      terminal = response;

      const bdAmount      = amount ? new BigDecimal(amount.toString()) : BigDecimal.ZERO;
      const amountInCents = bdAmount.multiply(new BigDecimal('100')).setScale(0, BigDecimal.ROUND_HALF_DOWN).intValueExact();

      const modal = this.ngbModal.open(AssemblyPaymentsTransactionComponent, { windowClass: 'md-modal' });
      modal.componentInstance.amount   = amountInCents;
      modal.componentInstance.invoice  = invoice;
      modal.componentInstance.isRefund = isRefund;
      modal.componentInstance.payment  = payment;
      modal.componentInstance.terminal = terminal;
      return modal.result;
    })
    .then(result => {
      data = result;

      return data.complete
        ? ( isRefund
            ? this.apiService.get('/invoice/process_refund', invoice.id)
            : this.apiService.post('/customer_payments/process_assembly_payments_payment', {
              payment_id: payment.id
            }, 'customer_payment')
          )
        : ( data.payment_status === 'incorrect_pin'
          ? Promise.reject('Incorrect Pin. Payment Cancelled.')
          : Promise.reject('Signature Declined. Payment Cancelled.')
        );
    })
    .then(() => {
      return data
        ? this.apiService[data.receipt_id ? 'patch' : 'post']('/eft_receipt', {
          customer_receipt: data.customer_receipt,
          invoice_id      : invoice.id,
          merchant_receipt: data.merchant_receipt,
          payment_id      : payment.id,
          payment_status  : data.payment_status,
          payment_type    : invoice.cash_or_account === 'C' ? 'cash' : 'account',
          pos_terminal_id : terminal.id,
          receipt_id      : data.receipt_id
        }, 'eft_receipt')
        : Promise.reject(this.translateService.instant('JS_SPACE.MESSAGES.CANCELED', {
          name: 'Payment'
        }));
    })
    .then(receipt => {
      if (terminal.ap_print_receipt_from_pos) {
        return Promise.resolve(true);
      }

      if (data.complete) {
        window.open(`${ API.url }/reports/jasper_eftpos_receipt/merchant/${ receipt.id }.pdf`, '_blank' );
      }

      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.PRINT_COPY'))
      .then(() => {
        window.open(`${ API.url }/reports/jasper_eftpos_receipt/customer/${ receipt.id }.pdf`, '_blank' );
      });

      return Promise.resolve(receipt.id);
    });
  }

  removeTerminalFromStorage () : void {
    localStorage.removeItem(this.storageKey);
  }

  saveTerminal ( terminal : AssemblyPaymentTerminalModel ) : Promise<AssemblyPaymentTerminalModel> {
    terminal.alternate_ip_address = terminal.ap_ip_address;

    return terminal.id
      ? this.apiService.patch('/pos_terminal', terminal, 'pos_terminal')
      : this.apiService.post('/pos_terminal', terminal, 'pos_terminal');
  }

  saveTestMode ( integrations : CompanyIntegrationsModel ) : Promise<CompanyIntegrationsModel> {
    return this.apiService.patch('/company_integrations', integrations, 'company_integration');
  }

  setReceiptOptions () : void {
    this.receiptOptions = new TransactionOptions();

    this.receiptOptions.SetMerchantReceiptHeader('');
    this.receiptOptions.SetMerchantReceiptFooter('');
    this.receiptOptions.SetCustomerReceiptHeader('');
    this.receiptOptions.SetCustomerReceiptFooter('');
  }

  setupListeners () {
    document.addEventListener('SecretsChanged', ( e : CustomEvent ) => {
      const currentTerminal = this.getTerminal();

      if (!e.detail && currentTerminal) {
        this.apiService.get('/pos_terminal/clear_keys_from_assembly_payments', currentTerminal.id)
        .then(() => {
          this.removeTerminalFromStorage();

          window.location.reload();
        });
      };
    });

    document.addEventListener('StatusChanged', ( e : CustomEvent ) => {
      const currentTerminal = this.getTerminal();

      if (currentTerminal) {
        if (currentTerminal.status !== e.detail) {
          currentTerminal.status = e.detail;

          this.storeTerminal(currentTerminal);
        };
      }
      else {
        const terminal = {
          alternate_ip_address: this.spiApi._eftposAddress,
          status              : e.detail
        };

        this.storeTerminal(terminal);
      };
    });
  }

  storeTerminal ( terminal : AssemblyPaymentTerminalModel ) : void {
    localStorage.setItem(this.storageKey, JSON.stringify(terminal));
  }

  unpairTerminal ( terminal : AssemblyPaymentTerminalModel = this.getTerminal(), options : AssemblyPaymentsUnpairOptionsModel = {} ) : Promise<boolean> {
    const errorMessage = 'Cannot find terminal to unpair. Process canceled.';

    return Promise.resolve()
    .then(() => {
      if ( !terminal || !this.spiApi ) {
        return Promise.reject(errorMessage);
      }

      return options.skipConfirmation
        ? Promise.resolve(true)
        : this.confirmService.generic(this.translateService.instant('ASSEMBLY_PAYMENTS_SPACE.UNPAIR'));
    })
    .then(() => {
      this.spiApi.AckFlowEndedAndBackToIdle();

      return this.spiApi.Unpair();
    })
    .then(() => {
      if ( this.spiApi._currentStatus === 'Unpaired' ) {
        this.removeTerminalFromStorage();

        return Promise.resolve(true);
      }

      return Promise.reject(errorMessage);
    });
  }
}
